/**
 * 流式消息更新器
 */
import type { Ref } from 'vue';
import type { AgentMessageResponse } from '@/types/formulation/formulation';
import { STREAMING_TOOL_DISPLAY_ID, TASK_STATUS } from '../types';
import type { MessageUpdater, StreamContext } from './types';

/** 临时 ID 偏移，当 ListAgentMessage 中尚无相同 tool_key 时使用 maxId + 5000 */
const STREAMING_TEMP_ID_OFFSET = 10000;

/** 流式消息状态 */
interface StreamingMessageState {
  content: string;
  messageId: number;
  status: 'running' | 'completed';
}

export interface MessageUpdaterOptions {
  /** 会话数据 */
  conversationData: Ref<AgentMessageResponse[] | undefined>;
  /** 选中的工具 key */
  selectedToolKey: Ref<string>;
  /** 设置选中的工具 */
  setSelectedTool: (toolKey: string, agentMessageId: number) => void;
}

/**
 * 创建消息更新器
 */
export function createMessageUpdater(
  options: MessageUpdaterOptions,
  streamContext: StreamContext,
): MessageUpdater {
  const { conversationData, selectedToolKey, setSelectedTool } = options;
  const { sessionIdValue, streamIteration, roundStartIso } = streamContext;

  let streamingToolContent = '';
  let streamingToolKey = '';
  /** 每个 tool_key 的流式消息状态，支持同一 tool_key 多次出现 */
  const streamingStateByToolKey = new Map<string, StreamingMessageState>();
  /** 临时消息 ID 计数器 */
  let tempIdCounter = 0;

  /**
   * 获取下一个临时消息 ID
   */
  const getNextTempId = (): number => {
    const list = conversationData.value ?? [];
    const ids = list.map((m) => m.id).filter((id) => Number.isFinite(id) && id > 0);
    const maxId = ids.length > 0 ? Math.max(...ids) : 0;
    return maxId + STREAMING_TEMP_ID_OFFSET + ++tempIdCounter;
  };

  /**
   * 更新或插入消息到列表
   */
  const upsertMessage = (messageId: number, message: AgentMessageResponse) => {
    const existingMessages = conversationData.value ?? [];
    const idx = existingMessages.findIndex((msg) => msg.id === messageId);

    if (idx >= 0) {
      const newMessages = [...existingMessages];
      newMessages[idx] = message;
      conversationData.value = newMessages;
    } else {
      conversationData.value = [...existingMessages, message];
    }
  };

  /**
   * 更新流式 AI 消息（累积内容）
   * 支持同一 tool_key 多次出现：当状态为 completed 后再次收到数据时，创建新的消息实例
   */
  const updateStreamingMessage = (
    newContent: string,
    type: string,
    isCompleted: boolean = false,
    fullContent?: string,
  ) => {
    const existingState = streamingStateByToolKey.get(type);

    // 判断是否需要创建新的消息实例：不存在状态 或 上一个已完成
    const needNewInstance = !existingState || existingState.status === 'completed';

    let state: StreamingMessageState;
    if (needNewInstance) {
      // 创建新的消息实例
      state = {
        content: '',
        messageId: getNextTempId(),
        status: 'running',
      };
      streamingStateByToolKey.set(type, state);
    } else {
      state = existingState;
    }

    // 更新内容
    if (isCompleted && fullContent !== undefined) {
      state.content = fullContent;
    } else {
      state.content += newContent;
    }

    // 更新状态
    if (isCompleted) {
      state.status = 'completed';
    }

    const streamingMessage: AgentMessageResponse = {
      id: state.messageId,
      business_type: 'user_message',
      session_id: sessionIdValue,
      iteration: streamIteration,
      tool_key: type,
      content: { raw: state.content },
      tool_state: isCompleted ? TASK_STATUS.COMPLETED : TASK_STATUS.RUNNING,
      gmt_created: roundStartIso,
    };

    upsertMessage(state.messageId, streamingMessage);
  };

  /**
   * 更新右侧流式 tool_display
   */
  const updateStreamingToolDisplay = (toolKey: string, token: string) => {
    if (!toolKey) return;

    if (streamingToolKey !== toolKey) {
      streamingToolKey = toolKey;
      streamingToolContent = '';
    }

    if (token) {
      streamingToolContent += token;
    }

    const toolDisplayMessage: AgentMessageResponse = {
      id: STREAMING_TOOL_DISPLAY_ID,
      business_type: 'tool_display',
      session_id: sessionIdValue,
      iteration: streamIteration,
      tool_key: streamingToolKey,
      content: { raw: streamingToolContent },
      tool_state: TASK_STATUS.RUNNING,
      gmt_created: roundStartIso,
    };

    upsertMessage(STREAMING_TOOL_DISPLAY_ID, toolDisplayMessage);

    if (selectedToolKey.value !== streamingToolKey) {
      setSelectedTool(streamingToolKey, STREAMING_TOOL_DISPLAY_ID);
    }
  };

  /**
   * 重置累积内容及状态缓存
   */
  const reset = () => {
    streamingToolContent = '';
    streamingToolKey = '';
    streamingStateByToolKey.clear();
    tempIdCounter = 0;
  };

  return {
    updateStreamingMessage,
    updateStreamingToolDisplay,
    reset,
  };
}
